import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { NO_DATA } from "@/constants";
import { formatNumber } from "@/lib/utils/formatters";
import { cn } from "@/lib/utils";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { ProjectSchedulesVisitEditor } from "@/modules/projects/components/schedules/ProjectSchedulesVisitEditor";
import {
  VISIT_STATUS_NEW,
  VISIT_STATUS_STARTED,
  VISIT_STATUS_COMPLETED,
  VISIT_STATUS_CANCELLED,
} from "@/schemas/contractorVisit";
import {
  ReadProjectSchedule,
  ReadProjectScheduleVisit,
} from "@/types/projectSchedule";
import { memo, useState } from "react";
import { ProjectSchedulesVisitPreview } from "./ProjectSchedulesVisitPreview";
import { PopoverArrow } from "@radix-ui/react-popover";

interface ProjectSchedulesVisitPopoverProps {
  scheduleVisitData: ReadProjectScheduleVisit;
  scheduleData: ReadProjectSchedule;
}

const ProjectSchedulesVisitPopoverComponent = ({
  scheduleVisitData,
  scheduleData,
}: ProjectSchedulesVisitPopoverProps) => {
  const { can_update, can_create } = useModulePermissions();
  const [open, isOpen] = useState(false);

  const isNew = scheduleVisitData.status === VISIT_STATUS_NEW;
  const isStarted = scheduleVisitData?.status === VISIT_STATUS_STARTED;
  const isCompleted = scheduleVisitData?.status === VISIT_STATUS_COMPLETED;
  const isCanceled = scheduleVisitData?.status === VISIT_STATUS_CANCELLED;
  const hasPrPlannedDuration =
    scheduleVisitData?.pr_planned_duration !== null &&
    scheduleVisitData?.pr_planned_duration !== undefined;

  const isEditorActive = isNew || isCompleted || isCanceled;
  const isPreviewActive = isStarted;

  if (scheduleVisitData ? !can_update : !can_create) {
    return (
      <div
        className={cn("flex flex-row gap-2 justify-center", {
          "text-gray-400": !hasPrPlannedDuration,
          "text-yellow-800": hasPrPlannedDuration,
        })}
      >
        {scheduleVisitData?.kam_planned_duration
          ? formatNumber(scheduleVisitData.kam_planned_duration)
          : NO_DATA}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={isOpen} modal>
      <div className="flex flex-row gap-2 justify-center">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn("p-1 h-6 min-w-6", {
              "border-2 border-slate-950": open,
              "border-green-700 bg-green-200 text-green-800":
                isStarted || isCompleted,
              "border-red-700 bg-red-200 text-red-800": isCanceled,
              "border-yellow-600 bg-yellow-200 text-yellow-800":
                hasPrPlannedDuration &&
                !isStarted &&
                !isCompleted &&
                !isCanceled,
            })}
          >
            {scheduleVisitData?.kam_planned_duration
              ? formatNumber(scheduleVisitData.kam_planned_duration)
              : NO_DATA}
          </Button>
        </PopoverTrigger>
      </div>

      <PopoverContent className="w-auto min-w-[260px]">
        {isEditorActive && (
          <ProjectSchedulesVisitEditor
            projectSchedule={scheduleData}
            projectScheduleVisit={scheduleVisitData}
          />
        )}
        {isPreviewActive && (
          <ProjectSchedulesVisitPreview
            projectSchedule={scheduleData}
            projectScheduleVisit={scheduleVisitData}
          />
        )}
        <PopoverArrow className="fill-slate-300" />
      </PopoverContent>
    </Popover>
  );
};

export const ProjectSchedulesVisitPopover = memo(
  ProjectSchedulesVisitPopoverComponent,
);
